import { parsePath, morph, polyLength, truncate } from "./pathgeom.ts";
import type { HtmlElement, ImageElement, ParticlesElement, ShapeElement, Styled, SubtitleStyle, TextElement, VideoElement } from "./types.ts";

/** A rasterised element. width/height are the CARD size in CSS px (content + padding); the bitmap is supersampled. */
export interface Unit { x: number; y: number; w: number; h: number }
export interface Card {
  canvas: HTMLCanvasElement | OffscreenCanvas; width: number; height: number;
  /** Text only: boxes (card CSS px, top-left origin) of each character / word / line, for staggered reveals. */
  units?: { chars: Unit[]; words: Unit[]; lines: Unit[] };
}
export type Layer = "glyph" | "shadow";

const FONTS = {
  sans: '"Inter", "Helvetica Neue", Helvetica, Arial, "DejaVu Sans", sans-serif',
  serif: 'Georgia, "Times New Roman", "DejaVu Serif", serif',
  mono: '"SF Mono", Menlo, Consolas, "DejaVu Sans Mono", monospace',
} as const;
export const SUPERSAMPLE = 2;
const PAD = 4;
const MAX_BITMAP = 4096;

function makeCanvas(w: number, h: number): HTMLCanvasElement {
  const c = document.createElement("canvas"); c.width = w; c.height = h; return c;
}


// ---------------------------------------------------------------------------------------------------------------
// Text and shapes: gradient fill, stroke, drop shadow / glow, extra shapes. Every element is rasterised as up to two
// cards of identical size: the shadow layer (only when a shadow is set) and the glyph layer.
// ---------------------------------------------------------------------------------------------------------------
interface Sty { fillTo: string | null; angle: number; fillA: number; stroke: string | null; strokeW: number;
  shColor: string | null; shBlur: number; shX: number; shY: number; shA: number }
export function styleOf(el: Styled): Sty {
  return { fillTo: el.fill_to ?? null, angle: el.fill_angle ?? 90, fillA: el.fill_opacity ?? 1, stroke: el.stroke ?? null,
           strokeW: el.stroke ? el.stroke_width ?? 0 : 0, shColor: el.shadow_color ?? null, shBlur: el.shadow_blur ?? 0,
           shX: el.shadow_x ?? 0, shY: el.shadow_y ?? 0, shA: el.shadow_opacity ?? 0.6 };
}
/** How far effects reach beyond the outline (px). */
const reach = (s: Sty): number => Math.max(s.strokeW / 2, s.shColor ? s.shBlur * 1.4 + Math.max(Math.abs(s.shX), Math.abs(s.shY)) : 0);
export const hasShadow = (el: Styled): boolean => !!el.shadow_color && ((el.shadow_opacity ?? 0.6) > 0);

function rgba(hex: string, a: number): string {
  const [r, g, b] = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
  return `rgba(${r},${g},${b},${a})`;
}
function gradient(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, c1: string, c2: string, angleDeg: number): CanvasGradient {
  const a = (angleDeg * Math.PI) / 180, dx = Math.cos(a), dy = Math.sin(a), cx = x + w / 2, cy = y + h / 2;
  const half = (Math.abs(w * dx) + Math.abs(h * dy)) / 2 || 1;
  const g = ctx.createLinearGradient(cx - dx * half, cy - dy * half, cx + dx * half, cy + dy * half);
  g.addColorStop(0, c1); g.addColorStop(1, c2);
  return g;
}
const bitmapScale = (w: number, h: number): number => Math.min(SUPERSAMPLE, MAX_BITMAP / Math.max(w, h, 1));
const SHADOW_OFF = 30000;      // the shadow layer draws the shape far off-canvas and shifts only its shadow back in

function setFont(ctx: CanvasRenderingContext2D, el: TextElement): void {
  ctx.font = `${el.font_weight} ${el.font_size}px ${FONTS[el.font_family]}`;
  (ctx as unknown as { letterSpacing: string }).letterSpacing = `${el.letter_spacing ?? 0}px`;
}

export interface TextLayout { lines: string[]; widths: number[]; width: number; height: number; lh: number; pad: number }
export function textLayout(el: TextElement, ctx: CanvasRenderingContext2D): TextLayout {
  setFont(ctx, el);
  const lines: string[] = [];
  for (const para of el.text.split("\n")) {
    let line = "";
    for (const word of para.split(/\s+/).filter(Boolean)) {
      const test = line ? `${line} ${word}` : word;
      if (line && ctx.measureText(test).width > el.max_width) { lines.push(line); line = word; } else line = test;
    }
    lines.push(line);
  }
  const widths = lines.map((l) => Math.min(el.max_width, ctx.measureText(l).width));
  const lh = el.font_size * (el.line_height ?? 1.2);
  const width = Math.max(1, Math.ceil(Math.max(...widths)));
  const pad = Math.ceil(PAD + 8 + el.font_size * 0.25 + reach(styleOf(el)));
  return { lines, widths, width, height: Math.max(1, Math.ceil(lines.length * lh)), lh, pad };
}

function lineStart(el: TextElement, L: TextLayout, i: number): number {
  const w = L.widths[i]!;
  return el.align === "left" ? L.pad : el.align === "right" ? L.pad + L.width - w : L.pad + (L.width - w) / 2;
}

function textUnits(el: TextElement, L: TextLayout, ctx: CanvasRenderingContext2D): NonNullable<Card["units"]> {
  setFont(ctx, el);
  const chars: Unit[] = [], words: Unit[] = [], lines: Unit[] = [];
  const H = Math.max(L.lh, el.font_size * 1.3);
  L.lines.forEach((line, i) => {
    const x0 = lineStart(el, L, i), y = L.pad + L.lh * i + L.lh / 2 - H / 2;
    lines.push({ x: x0 - 2, y, w: L.widths[i]! + 4, h: H });
    for (const m of line.matchAll(/\S+/g)) {
      const before = ctx.measureText(line.slice(0, m.index)).width, w = ctx.measureText(m[0]).width;
      words.push({ x: x0 + before - 2, y, w: w + 4, h: H });
    }
    for (let k = 0; k < line.length; k++) {
      if (/\s/.test(line[k]!)) continue;
      const a = ctx.measureText(line.slice(0, k)).width, b = ctx.measureText(line.slice(0, k + 1)).width;
      chars.push({ x: x0 + a - 1, y, w: Math.max(2, b - a) + 2, h: H });
    }
  });
  return { chars, words, lines };
}

/** Rasterise a text element. layer "glyph" = fill + stroke; layer "shadow" = only its shadow / glow. */
export function rasterText(el: TextElement, layer: Layer = "glyph"): Card {
  const probe = makeCanvas(1, 1).getContext("2d")!;
  const L = textLayout(el, probe), st = styleOf(el);
  const width = L.width + L.pad * 2, height = L.height + L.pad * 2, ss = bitmapScale(width, height);
  const canvas = makeCanvas(Math.max(1, Math.round(width * ss)), Math.max(1, Math.round(height * ss)));
  const ctx = canvas.getContext("2d")!;
  ctx.scale(ss, ss);
  setFont(ctx, el);
  ctx.textBaseline = "middle"; ctx.textAlign = "left";
  const fillStyle: string | CanvasGradient = st.fillTo ? gradient(ctx, L.pad, L.pad, L.width, L.height, el.fill, st.fillTo, st.angle) : el.fill;
  const shadow = layer === "shadow";
  if (shadow) {
    ctx.shadowColor = rgba(st.shColor ?? "#000000", st.shA);
    ctx.shadowBlur = st.shBlur * ss; ctx.shadowOffsetX = (SHADOW_OFF + st.shX) * ss; ctx.shadowOffsetY = st.shY * ss;
  }
  const dx = shadow ? -SHADOW_OFF : 0;
  const passes = shadow && st.shBlur >= 12 ? 2 : 1;              // glows need to be punchier than a plain blur
  for (let pass = 0; pass < passes; pass++) {
    L.lines.forEach((l, i) => {
      const x = lineStart(el, L, i) + dx, y = L.pad + L.lh * i + L.lh / 2;
      if (st.stroke && st.strokeW > 0) {
        ctx.lineJoin = "round"; ctx.lineWidth = st.strokeW; ctx.strokeStyle = st.stroke; ctx.strokeText(l, x, y);
      }
      if (st.fillA > 0) { ctx.globalAlpha = st.fillA; ctx.fillStyle = fillStyle; ctx.fillText(l, x, y); ctx.globalAlpha = 1; }
    });
  }
  const card: Card = { canvas, width, height };
  if (!shadow && (el.split ?? "none") !== "none") card.units = textUnits(el, L, probe);
  return card;
}

function shapePath(ctx: CanvasRenderingContext2D, el: ShapeElement, x: number, y: number, w: number, h: number): void {
  const cx = x + w / 2, cy = y + h / 2;
  ctx.beginPath();
  switch (el.shape) {
    case "ellipse": ctx.ellipse(cx, cy, w / 2, h / 2, 0, 0, Math.PI * 2); break;
    case "ring": {
      ctx.ellipse(cx, cy, w / 2, h / 2, 0, 0, Math.PI * 2);
      const r = el.inner_ratio ?? 0.5;
      ctx.moveTo(cx + (w / 2) * r, cy); ctx.ellipse(cx, cy, (w / 2) * r, (h / 2) * r, 0, 0, Math.PI * 2);
      break;
    }
    case "polygon": case "star": {
      const n = Math.max(3, el.sides ?? 5), star = el.shape === "star", r = el.inner_ratio ?? 0.5, count = star ? n * 2 : n;
      for (let i = 0; i < count; i++) {
        const k = star && i % 2 ? r : 1, a = -Math.PI / 2 + (i * Math.PI * 2) / count;
        const px = cx + Math.cos(a) * (w / 2) * k, py = cy + Math.sin(a) * (h / 2) * k;
        if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
      }
      ctx.closePath();
      break;
    }
    default: ctx.roundRect(x, y, w, h, Math.min(el.corner_radius, w / 2, h / 2));
  }
}

/** Line-drawn / morphing vector paths. `draw` (0..1) is how much of the outline is stroked, `morph` (0..1) blends path_d into path_to. */
export function rasterPath(el: ShapeElement, layer: Layer, draw: number, morphU: number): Card {
  const w = Math.max(1, Math.round(el.width)), h = Math.max(1, Math.round(el.height)), st = styleOf(el);
  const lineW = st.strokeW > 0 ? st.strokeW : 6, reachPx = Math.max(reach(st), lineW / 2);
  const pad = Math.ceil(PAD + 12 + reachPx), width = w + pad * 2, height = h + pad * 2, ss = bitmapScale(width, height);
  const canvas = makeCanvas(Math.max(1, Math.round(width * ss)), Math.max(1, Math.round(height * ss)));
  const ctx = canvas.getContext("2d")!;
  ctx.scale(ss, ss);
  const shadow = layer === "shadow";
  if (shadow) {
    ctx.shadowColor = rgba(st.shColor ?? "#000000", st.shA);
    ctx.shadowBlur = st.shBlur * ss; ctx.shadowOffsetX = (SHADOW_OFF + st.shX) * ss; ctx.shadowOffsetY = st.shY * ss;
  }
  const dx = shadow ? -SHADOW_OFF : 0;
  let polys = parsePath(el.path_d ?? "");
  if (el.path_to && morphU > 0) polys = morph(polys, parsePath(el.path_to), morphU);
  const stroke = st.stroke ?? el.fill, fillIt = !!el.path_fill && st.fillA > 0;
  ctx.lineJoin = "round"; ctx.lineCap = el.path_cap ?? "round"; ctx.lineWidth = lineW;
  const passes = shadow && st.shBlur >= 12 ? 2 : 1;
  for (let pass = 0; pass < passes; pass++) {
    if (fillIt && draw >= 1) {
      ctx.beginPath();
      for (const p of polys) { ctx.moveTo(pad + dx + p.pts[0]!, pad + p.pts[1]!); for (let i = 2; i < p.pts.length; i += 2) ctx.lineTo(pad + dx + p.pts[i]!, pad + p.pts[i + 1]!); if (p.closed) ctx.closePath(); }
      ctx.globalAlpha = st.fillA; ctx.fillStyle = el.fill; ctx.fill(); ctx.globalAlpha = 1;
    }
    if (draw <= 0) continue;
    ctx.strokeStyle = shadow ? "#000000" : stroke;
    // `draw` runs over the TOTAL length of all subpaths, so several strokes are drawn one after another.
    const lens = polys.map(polyLength), total = lens.reduce((a, b) => a + b, 0);
    let budget = Math.min(1, draw) * total;
    for (let k = 0; k < polys.length && budget > 0; k++) {
      const p = polys[k]!, L = lens[k]!, frac = L === 0 ? 1 : Math.min(1, budget / L);
      const pts = truncate(p, frac);
      ctx.beginPath(); ctx.moveTo(pad + dx + pts[0]!, pad + pts[1]!);
      for (let i = 2; i < pts.length; i += 2) ctx.lineTo(pad + dx + pts[i]!, pad + pts[i + 1]!);
      ctx.stroke();
      budget -= L;
    }
  }
  return { canvas, width, height };
}

export function rasterShape(el: ShapeElement, layer: Layer = "glyph"): Card {
  const w = Math.max(1, Math.round(el.width)), h = Math.max(1, Math.round(el.height)), st = styleOf(el);
  const pad = Math.ceil(PAD + 12 + reach(st)), width = w + pad * 2, height = h + pad * 2, ss = bitmapScale(width, height);
  const canvas = makeCanvas(Math.max(1, Math.round(width * ss)), Math.max(1, Math.round(height * ss)));
  const ctx = canvas.getContext("2d")!;
  ctx.scale(ss, ss);
  const shadow = layer === "shadow";
  if (shadow) {
    ctx.shadowColor = rgba(st.shColor ?? "#000000", st.shA);
    ctx.shadowBlur = st.shBlur * ss; ctx.shadowOffsetX = (SHADOW_OFF + st.shX) * ss; ctx.shadowOffsetY = st.shY * ss;
  }
  const dx = shadow ? -SHADOW_OFF : 0, rule = el.shape === "ring" ? "evenodd" : "nonzero";
  const passes = shadow && st.shBlur >= 12 ? 2 : 1;
  for (let pass = 0; pass < passes; pass++) {
    shapePath(ctx, el, pad + dx, pad, w, h);
    if (st.fillA > 0 || shadow) {
      ctx.globalAlpha = st.fillA > 0 ? st.fillA : 1;
      ctx.fillStyle = st.fillTo ? gradient(ctx, pad + dx, pad, w, h, el.fill, st.fillTo, st.angle) : el.fill;
      if (!(shadow && st.fillA === 0 && st.strokeW > 0)) ctx.fill(rule);
      ctx.globalAlpha = 1;
    }
    if (st.stroke && st.strokeW > 0) { ctx.lineJoin = "round"; ctx.lineWidth = st.strokeW; ctx.strokeStyle = st.stroke; ctx.stroke(); }
  }
  return { canvas, width, height };
}

/** Pictures: the same card pipeline as shapes (rounded corners, stroke, shadow/glow), the bitmap is the clipped, fitted image.
 *  `img` is null while the file is still loading: a neutral placeholder is drawn and the card is rebuilt once it is ready. */
export function rasterImage(el: ImageElement | VideoElement, img: HTMLImageElement | HTMLVideoElement | null, layer: Layer = "glyph"): Card {
  const w = Math.max(1, Math.round(el.width)), h = Math.max(1, Math.round(el.height)), st = styleOf(el);
  const pad = Math.ceil(PAD + 12 + reach(st)), width = w + pad * 2, height = h + pad * 2, ss = bitmapScale(width, height);
  const canvas = makeCanvas(Math.max(1, Math.round(width * ss)), Math.max(1, Math.round(height * ss)));
  const ctx = canvas.getContext("2d")!;
  ctx.scale(ss, ss);
  const shadow = layer === "shadow";
  if (shadow) {
    ctx.shadowColor = rgba(st.shColor ?? "#000000", st.shA);
    ctx.shadowBlur = st.shBlur * ss; ctx.shadowOffsetX = (SHADOW_OFF + st.shX) * ss; ctx.shadowOffsetY = st.shY * ss;
  }
  const dx = shadow ? -SHADOW_OFF : 0, r = Math.min(el.corner_radius, w / 2, h / 2);
  const passes = shadow && st.shBlur >= 12 ? 2 : 1;
  for (let pass = 0; pass < passes; pass++) {
    ctx.beginPath(); ctx.roundRect(pad + dx, pad, w, h, r);
    if (shadow) { ctx.fillStyle = "#000000"; ctx.fill(); }
    else {
      ctx.save(); ctx.clip();
      const iw = img ? ("videoWidth" in img ? img.videoWidth : img.naturalWidth) : 0;
      const ih = img ? ("videoHeight" in img ? img.videoHeight : img.naturalHeight) : 0;
      if (!img || !iw || !ih) { ctx.fillStyle = "rgba(128,128,140,0.35)"; ctx.fillRect(pad, pad, w, h); }
      else {
        ctx.imageSmoothingQuality = "high";
        if (el.fit === "fill") ctx.drawImage(img, pad, pad, w, h);
        else {
          const s = el.fit === "contain" ? Math.min(w / iw, h / ih) : Math.max(w / iw, h / ih), dw = iw * s, dh = ih * s;
          ctx.drawImage(img, pad + (w - dw) / 2, pad + (h - dh) / 2, dw, dh);
        }
      }
      ctx.restore();
    }
    if (st.stroke && st.strokeW > 0) {
      ctx.beginPath(); ctx.roundRect(pad + dx, pad, w, h, r);
      ctx.lineJoin = "round"; ctx.lineWidth = st.strokeW; ctx.strokeStyle = st.stroke; ctx.stroke();
    }
  }
  return { canvas, width, height };
}

/** Cache key of everything that changes the bitmap (not transform or reveal values). */
export function cardKey(el: TextElement | ShapeElement | ImageElement, layer: Layer, ready = true, draw = 1, morphU = 0): string {
  const common = [layer, "fill" in el ? el.fill : null, el.fill_to, el.fill_angle, el.fill_opacity, el.stroke, el.stroke_width, el.shadow_color, el.shadow_blur,
                  el.shadow_x, el.shadow_y, el.shadow_opacity];
  const own = el.type === "text"
    ? [el.text, el.font_family, el.font_size, el.font_weight, el.align, el.max_width, el.letter_spacing, el.line_height, el.split !== "none"]
    : el.type === "image"
      ? [el.asset_id, el.width, el.height, el.fit, el.corner_radius, ready]
      : el.shape === "path"
        ? [el.shape, el.width, el.height, el.path_d, el.path_to, el.path_fill, el.path_cap, Math.round(draw * 240), Math.round(morphU * 240)]
        : [el.shape, el.width, el.height, el.corner_radius, el.sides, el.inner_ratio];
  return el.type + "|" + JSON.stringify([...common, ...own]);
}

/** Size in world px of the visible content of an element (before its own scale) - used by the editor for selection boxes. */
export function measureCard(el: TextElement | ShapeElement | ImageElement | VideoElement | ParticlesElement | HtmlElement): { width: number; height: number } {
  if (el.type === "particles") return { width: Math.max(1, el.area_w), height: Math.max(1, el.area_h) };
  if (el.type === "shape" || el.type === "image" || el.type === "video" || el.type === "html") return { width: Math.max(1, Math.round(el.width)), height: Math.max(1, Math.round(el.height)) };
  const ctx = makeCanvas(1, 1).getContext("2d")!;
  const { width, height } = textLayout(el, ctx);
  return { width: width + PAD * 2, height: height + PAD * 2 };
}

// ---- html: a static HTML+CSS document rasterised by the browser through an SVG image. Scripts, network and external files cannot run
// in that context; CSS animations are paused and seeked to the element clock, so the picture is a pure function of (element, time). ----
export const HTML_PAD = 24;                      // room around the box for glows / box-shadows
const HTML_PRELUDE = '*,*::before,*::after{animation-play-state:paused!important;animation-delay:'
  + [1, 2, 3, 4].map((i) => `calc(var(--d${i},var(--delay,0s)) - var(--t))`).join(',') + '!important}'
  + '.m-root{position:relative;font-family:"Inter","Helvetica Neue",Helvetica,Arial,"DejaVu Sans",sans-serif;color:#fff;line-height:1.25}';
export function htmlSvg(el: HtmlElement, clockMs: number, ss: number): string {
  const P = HTML_PAD, W = Math.round(el.width) + P * 2, H = Math.round(el.height) + P * 2, t = (clockMs / 1000).toFixed(4);
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${Math.round(W * ss)}" height="${Math.round(H * ss)}" viewBox="0 0 ${W} ${H}"><foreignObject x="0" y="0" width="${W}" height="${H}">`
    + `<div xmlns="http://www.w3.org/1999/xhtml" style="width:${W}px;height:${H}px;padding:${P}px;box-sizing:border-box;--t:${t}s;--ms:${Math.round(clockMs)}">`
    + `<style><![CDATA[${HTML_PRELUDE}${el.css}]]></style><div class="m-root" style="width:${Math.round(el.width)}px;height:${Math.round(el.height)}px">${el.markup}</div></div></foreignObject></svg>`;
}
export function htmlCardSize(el: HtmlElement): { width: number; height: number } { return { width: Math.round(el.width) + HTML_PAD * 2, height: Math.round(el.height) + HTML_PAD * 2 }; }
/** Transparent placeholder with the right size, drawn until the real picture has decoded. */
export function blankHtmlCard(el: HtmlElement): Card { const c = makeCanvas(2, 2); return { canvas: c, ...htmlCardSize(el) }; }
export async function rasterHtml(el: HtmlElement, clockMs: number): Promise<Card> {
  const { width, height } = htmlCardSize(el), ss = bitmapScale(width, height);
  const img = new Image();
  img.src = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(htmlSvg(el, clockMs, ss));
  try { await img.decode(); } catch { return blankHtmlCard(el); }         // a document the browser cannot draw shows nothing rather than failing the frame
  const canvas = makeCanvas(Math.max(1, Math.round(width * ss)), Math.max(1, Math.round(height * ss)));
  // CPU-backed canvas on purpose: drawing an SVG foreignObject on an accelerated canvas (SwiftShader) made successive
  // rasters drift lighter in the exporter; the software path is deterministic.
  canvas.getContext("2d", { willReadFrequently: true })!.drawImage(img, 0, 0, canvas.width, canvas.height);
  return { canvas, width, height };
}
export const htmlKey = (el: HtmlElement, clockMs: number): string => `h|${el.id}|${Math.round(clockMs * 10)}|${el.width}|${el.height}|${hashStr(el.markup + "\u0000" + el.css)}`;
function hashStr(s: string): string { let h = 5381; for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0; return (h >>> 0).toString(36) + s.length; }

// ---- subtitles: a rounded box + centred, wrapped text, sized from the project's own resolution ----
function captionLayout(text: string, st: SubtitleStyle, canvasW: number, ctx: CanvasRenderingContext2D) {
  const padX = st.font_size * 0.5, padY = st.font_size * 0.28, lh = st.font_size * 1.25;
  ctx.font = `${st.font_weight} ${st.font_size}px ${FONTS[st.font_family]}`;
  const maxText = Math.max(st.font_size, canvasW * st.max_width - padX * 2);
  const lines: string[] = [];
  for (const para of text.split("\n")) {
    let line = "";
    for (const word of para.split(/\s+/).filter(Boolean)) {
      const test = line ? `${line} ${word}` : word;
      if (line && ctx.measureText(test).width > maxText) { lines.push(line); line = word; } else line = test;
    }
    lines.push(line);
  }
  const w = Math.ceil(Math.min(maxText, Math.max(...lines.map((l) => ctx.measureText(l).width))) + padX * 2);
  return { lines, width: Math.max(1, w), height: Math.ceil(lines.length * lh + padY * 2), padX, padY, lh };
}

export function rasterCaption(text: string, st: SubtitleStyle, canvasW: number): Card {
  const L = captionLayout(text, st, canvasW, makeCanvas(1, 1).getContext("2d")!);
  const canvas = makeCanvas(L.width * SUPERSAMPLE, L.height * SUPERSAMPLE);
  const ctx = canvas.getContext("2d")!;
  ctx.scale(SUPERSAMPLE, SUPERSAMPLE);
  const [r, g, b] = [1, 3, 5].map((i) => parseInt(st.background.slice(i, i + 2), 16));
  ctx.fillStyle = `rgba(${r},${g},${b},${st.background_opacity})`;
  ctx.beginPath(); ctx.roundRect(0, 0, L.width, L.height, st.font_size * 0.3); ctx.fill();
  ctx.font = `${st.font_weight} ${st.font_size}px ${FONTS[st.font_family]}`;
  ctx.fillStyle = st.fill; ctx.textAlign = "center"; ctx.textBaseline = "middle";
  L.lines.forEach((l, i) => ctx.fillText(l, L.width / 2, L.padY + L.lh * i + L.lh / 2));
  return { canvas, width: L.width, height: L.height };
}
